// Small dense Matrix type (column-major-ish flat storage) used by the
// smoothedmask polyfit path. Converted from the AMD/prototypal original; the
// Matrix is an Array subclass (numeric this[i] storage + .fill/.length). The dead
// test()/debug blocks from the original were dropped.

const temp_mats: cachering[] = [];

class cachering extends Array<Matrix> {
  cur: number;

  constructor(func: () => Matrix, size: number) {
    super();
    this.cur = 0;

    for (let i = 0; i < size; i++) {
      this.push(func());
    }
  }

  next(): Matrix {
    const ret = this[this.cur];
    this.cur = (this.cur + 1) % this.length;

    return ret;
  }
}

function _get_temp_matrix(m: number, n: number): Matrix {
  if (n * m > 64) {
    return new Matrix(m, n);
  }

  const key = n + m * 512;

  if (!(key in temp_mats)) {
    temp_mats[key] = new cachering(function () {
      return new Matrix(m, n);
    }, 8);
  }

  return temp_mats[key].next();
}

class Matrix extends Array<number> {
  m: number;
  n: number;

  constructor(m: number, n?: number) {
    super(n == undefined ? m * m : m * n);

    this.m = m;
    this.n = n == undefined ? m : n;

    this.makeIdentity();
  }

  load(data: Matrix | number[] | number[][]): this {
    if (data == undefined) {
      //this is allowed
      console.log("eck?");
      return this;
    }

    if (data instanceof Matrix || typeof (data as number[])[0] == "number") {
      for (let i = 0; i < this.m * this.n; i++) {
        this[i] = (data as number[])[i];
      }
    } else if ((data as number[][])[0] instanceof Array) {
      const m = this.m;
      const n = this.n;

      for (let i = 0; i < m; i++) {
        for (let j = 0; j < n; j++) {
          this[m * j + i] = (data as number[][])[i][j];
        }
      }
    } else {
      console.trace("Warning, could not load matrix", this, data);
      throw new Error("Invalid data for matrix");
    }

    return this;
  }

  makeIdentity(): this {
    const m = this.m;
    const n = this.n;

    for (let i = 0; i < this.length; i++) {
      this[i] = 0.0;
    }

    for (let i = 0; i < n; i++) {
      this[i * m + i] = 1.0;
    }

    return this;
  }

  toString(dec?: number): string {
    const m = this.m;
    const n = this.n;

    if (dec == undefined) dec = 3.0;

    if (n == 1.0) {
      let ret = "[";
      for (let i = 0; i < this.length; i++) {
        if (i > 0) ret += ", ";

        if (this[i] == undefined) ret += "undefined";
        else ret += this[i].toFixed(dec);
      }
      ret += "]";

      return ret;
    } else {
      let ret = "";

      for (let j = 0; j < m; j++) {
        for (let i = 0; i < n; i++) {
          //display as rows
          const cell = this[j * m + i];

          if (i > 0) ret += ",";
          if (cell >= 0) ret += " ";

          let num = cell.toFixed(dec);
          if (cell == 0.0) {
            num = "   ";
          }

          ret += num;
        }
        ret += "\n";
      }

      ret += "\n";

      return ret;
    }
  }

  determinant(): number {
    const m = this.m;
    const n = this.n;
    let ret = 1;
    if (m != n) {
      throw new Error("numeric: det() only works on square matrices");
    }

    const A = _get_temp_matrix(m, n).load(this);
    const abs = Math.abs;

    function swaprow(M: Matrix, i1: number, i2: number) {
      for (let j = 0; j < m; j++) {
        const t = M[i1 * m + j];

        M[i1 * m + j] = M[i2 * m + j];
        M[i2 * m + j] = t;
      }
    }

    let i;
    let j;
    let k;
    let alpha;
    let k1;

    for (j = 0; j < n - 1; j++) {
      k = j;

      for (i = j + 1; i < n; i++) {
        if (abs(A[i * m + j]) > abs(A[k * m + j])) {
          k = i;
        }
      }

      if (k !== j) {
        swaprow(A, k, j);
        ret *= -1;
      }

      for (i = j + 1; i < n; i++) {
        alpha = A[i * m + j] / (0.00000000000000001 + A[j * m + j]);
        for (k = j + 1; k < n - 1; k += 2) {
          k1 = k + 1;
          A[i * m + k] -= A[j * m + k] * alpha;
          A[i * m + k1] -= A[j * m + k1] * alpha;
        }
        if (k !== n) {
          A[i * m + k] -= A[j * m + k] * alpha;
        }
      }
      if (A[j * m + j] === 0) {
        return 0;
      }
      ret *= A[j * m + j];
    }
    return ret * A[j * m + j];
  }

  multiply(B: Matrix): this {
    const m = this.m;
    const n = this.n;

    const A = _get_temp_matrix(m, n).load(this);
    for (let i = 0; i < m * n; i++) {
      A[i] = this[i];
    }

    for (let i = 0; i < m; i++) {
      for (let j = 0; j < n; j++) {
        let sum = 0.0;

        for (let k = 0; k < m; k++) {
          sum += A[i * m + k] * B[k * m + j];
        }

        this[i * m + j] = sum;
      }
    }

    return this;
  }

  transpose(): this {
    const m = this.m;
    const n = this.n;
    const A = _get_temp_matrix(m, n).load(this);

    for (let i = 0; i < m; i++) {
      for (let j = 0; j < n; j++) {
        this[i * m + j] = A[j * m + i];
      }
    }

    return this;
  }

  invert(): void {
    const m = this.m;
    const n = this.n;
    const abs = Math.abs;

    const A = _get_temp_matrix(m, n).load(this);
    const I = _get_temp_matrix(m, n).makeIdentity();

    let i;
    let j;
    let k;
    let x;

    function swaprow(M: Matrix, j1: number, j2: number) {
      for (let i2 = 0; i2 < m; i2++) {
        const t = M[j1 * m + i2];

        M[j1 * m + i2] = M[j2 * m + i2];
        M[j2 * m + i2] = t;
      }
    }

    for (j = 0; j < n; ++j) {
      let i0 = -1;
      let v0 = -1;

      for (i = j; i !== m; ++i) {
        k = abs(A[i * m + j]);
        if (k > v0) {
          i0 = i;
          v0 = k;
        }
      }

      swaprow(A, i0, j);
      swaprow(I, i0, j);

      x = A[j * m + j] + 0.000000001;

      for (k = j; k !== n; ++k) A[j * m + k] /= x;
      for (k = n - 1; k !== -1; --k) I[j * m + k] /= x;

      for (i = m - 1; i !== -1; --i) {
        if (i !== j) {
          x = A[i * m + j];

          for (k = j + 1; k !== n; ++k) A[i * m + k] -= A[j * m + k] * x;
          for (k = n - 1; k > 0; --k) {
            I[i * m + k] -= I[j * m + k] * x;
            --k;
            I[i * m + k] -= I[j * m + k] * x;
          }
          if (k === 0) I[i * m] -= I[j * m] * x;
        }
      }
    }

    this.load(I);
  }
}

export { Matrix, temp_mats, _get_temp_matrix };
